import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FdxError } from '@app/core/models/classes/fdx-error.class';
import { GetVaultRowsRequest } from '@app/vault/models/interfaces/get-vault-rows-request.interface';
import { VaultRowModel } from '@app/vault/models/vault-row.model';
import { VaultCreateResponseType } from '@app/vault/types/vault-create-response.type';
import { VaultDeleteResponseType } from '@app/vault/types/vault-delete-response.type';
import { VaultGetAllResponseType } from '@app/vault/types/vault-get-all-response.type';
import { VaultGetBcImportCredentialsResponseType } from '@app/vault/types/vault-get-bc-import-credentials-response.type';
import { VaultUpdateResponseType } from '@app/vault/types/vault-update-response.type';
import { Observable, catchError, throwError } from 'rxjs';
import { map } from 'rxjs/operators';


@Injectable({
    providedIn: 'root'
})
export class VaultDataService {

    private readonly http: HttpClient = inject(HttpClient);

    private baseVaultUrl(dbId: string): string {
        return `/api.php/dbs/${dbId}/vault`;
    }

    /**
     * This method gets all the vault records filtered for a database id and matching query parameters.
     * @param dbId
     * @param queryParams
     */
    getAll(dbId: string, queryParams?: GetVaultRowsRequest): Observable<VaultRowModel[]> {
        let params = new HttpParams();

        Object.entries(queryParams ?? {}).forEach(([key, value]: [string, string | number | undefined]) => {
            if (value !== undefined && value !== null) {
                params = params.set(key, value);
            }
        });

        return this.http.get<VaultGetAllResponseType>(this.baseVaultUrl(dbId), { params: params }).pipe(
            map((response) => {
                    return response.data.vault_rows.map((row) => new VaultRowModel(this.cleanVaultDates(row)));
                }
            ),
            catchError((response) => {

                if (response.error.success === false) {
                    return throwError(() => new FdxError('ERROR', response.error.data.error));
                }

                return throwError(() => new FdxError('UNKNOWN_ERROR', 'Error retrieving data'));

            })
        );

    }

    /**
     * This method gets the BigCommerce import credentials vault record for a database id.
     * @param dbId
     */
    getBcImportCredentialsVault(dbId: string): Observable<VaultRowModel | null> {
        return this.http.get<VaultGetBcImportCredentialsResponseType>(this.baseVaultUrl(dbId) + '/bc_import_credentials').pipe(
            map((response) => {
                const vaultRow = response.data?.vault_row;
                // The API returns an empty row when the database has no BigCommerce
                // credentials or the user cannot read the record.
                if (!vaultRow || Object.keys(vaultRow).length === 0) {
                    return null;
                }
                return new VaultRowModel(this.cleanVaultDates(vaultRow));
            }),
            catchError((response) => {
                if (response.error?.success === false) {
                    return throwError(() => new FdxError('ERROR', response.error?.data?.error ?? 'Error retrieving data'));
                }

                return throwError(() => new FdxError('UNKNOWN_ERROR', 'Error retrieving data'));
            })
        );
    }

    create(dbId, data): Observable<VaultCreateResponseType> {

        return this.http.post<VaultCreateResponseType>(this.baseVaultUrl(dbId), data).pipe(
            catchError((response, caught) => {

                if (response.error.success === false) {
                    return throwError(() => new FdxError('ERROR', response.error.data.error));
                }

                return throwError(() => new FdxError('UNKNOWN_ERROR', 'Error saving data'));

            })
        );

    }

    update(dbId, vaultId, data): Observable<VaultUpdateResponseType> {

        return this.http.put<VaultUpdateResponseType>(`${this.baseVaultUrl(dbId)}/${vaultId}`, data).pipe(
            catchError((response, caught) => {

                if (response.error.success === false) {
                    return throwError(() => new FdxError('ERROR', response.error.data.error));
                }

                return throwError(() => new FdxError('UNKNOWN_ERROR', 'Error updating data'));

            })
        );

    }

    delete(dbId: string, vaultId: number): Observable<VaultDeleteResponseType> {

        return this.http.delete<VaultDeleteResponseType>(`${this.baseVaultUrl(dbId)}/${vaultId}`).pipe(
            catchError((response, caught) => {
                return throwError(() => new FdxError('ERROR', 'Error performing operation'));
            })
        );

    }

    /**
     * The backend converts the dates from NULL to 0000-00-00, we want them as NULL.
      */
    private cleanVaultDates(row: unknown): unknown {
        if (row['expiration'] === '0000-00-00') {
            row['expiration'] = null;
        }

        return row;
    }

}
